import { useCallback } from 'react';
import { ApolloError } from '@apollo/client';

import {
  usePandaDocDocumentsQuery,
  PandaDocDocumentsQueryVariables,
  PandaDocDocumentType,
} from 'core/graphql';

import { isPermissionDeniedError } from './usePandaDocAnalytics';

export type PandaDocDocumentRow = Pick<
  PandaDocDocumentType,
  | 'id'
  | 'pk'
  | 'pandadocId'
  | 'name'
  | 'status'
  | 'docType'
  | 'course'
  | 'outcome'
  | 'ttsHours'
  | 'dateCreated'
  | 'dateModified'
  | 'dateSent'
  | 'dateCompleted'
>;

export type PandaDocDocumentsFilters = Omit<
  PandaDocDocumentsQueryVariables,
  'first' | 'after'
>;

interface IPandaDocDocumentsHook {
  data: PandaDocDocumentRow[];
  totalCount: number;
  loading: boolean;
  error?: ApolloError;
  permissionDenied: boolean;
  hasNextPage: boolean;
  operations: {
    fetchMore: () => void;
    refetch: () => void;
  };
}

/** Relay-cursor paginated list of PandaDoc documents for the table. */
export const usePandaDocDocuments = (
  filters: PandaDocDocumentsFilters = {},
  pageSize = 50,
): IPandaDocDocumentsHook => {
  const {
    data,
    loading,
    error,
    fetchMore,
    refetch,
  } = usePandaDocDocumentsQuery({
    variables: { ...filters, first: pageSize },
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });

  const connection = data?.pandadocDocuments;
  const edges = connection?.edges ?? [];

  const handleFetchMore = useCallback(() => {
    if (!connection?.pageInfo?.hasNextPage) {
      return;
    }

    fetchMore({
      variables: {
        ...filters,
        first: pageSize,
        after: connection.pageInfo.endCursor,
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult?.pandadocDocuments) {
          return prev;
        }

        return {
          pandadocDocuments: {
            ...fetchMoreResult.pandadocDocuments,
            edges: [
              ...(prev.pandadocDocuments?.edges ?? []),
              ...fetchMoreResult.pandadocDocuments.edges,
            ],
          },
        };
      },
    });
  }, [connection, fetchMore, filters, pageSize]);

  return {
    data: edges
      .map((edge) => edge?.node)
      .filter((node): node is PandaDocDocumentRow => Boolean(node)),
    totalCount: connection?.totalCount ?? 0,
    loading,
    error,
    permissionDenied: isPermissionDeniedError(error),
    hasNextPage: Boolean(connection?.pageInfo?.hasNextPage),
    operations: {
      fetchMore: handleFetchMore,
      refetch: () => {
        refetch();
      },
    },
  };
};
